/**
 * Ryan O'Dowd
 * 2022-03-21
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Keyboard,
  View,
} from 'react-native';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import EmptyScreen from '@components/common/EmptyScreen';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import Icon from '@react-native-vector-icons/material-design-icons';
import PassageDisplay from '@components/PassageDisplay';
import Popover from '@components/common/Popover';
import {
  ProgressBar,
} from 'react-native-paper';
import ScreenWrapper from '@containers/ScreenWrapper';
import SummaryScreen from './SummaryScreen';
import TestWrapper from './TestWrapper';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import minigames from '@utils/globals/minigames';
/* @TODO:
import {
  setTestDraft,
} from '@utils/store/actions';
*/
import utilities from '@utils/utilities';

const DIFFICULTY_MULTIPLIERS = {
  easy: 1,
  medium: 1.6,
  hard: 2.8,
};

const NUM_GAMES_IN_LESSON = 10;
const PASSAGE_OF_THE_DAY_BONUS = 20;

const Practice = () => {
  // @TODO: const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);

  const isTest = locationState.isTest || false;

  const [_currGameIndex, setCurrGameIndex] = useState(0);
  const [_numCorrectAnswers, setNumCorrectAnswers] = useState(0);
  const [_numVersesReviewed, setNumVersesReviewed] = useState(0);
  const [_testPassedVerseNums, setTestPassedVerseNums] = useState([]);
  const [_multiVerseBonus, setMultiVerseBonus] = useState(0);
  const [_levelUpBonus, setLevelUpBonus] = useState(0);
  const [_numSkippedQuestions, setNumSkippedQuestions] = useState(0);
  const [_numVersesLeveledUp, setNumVersesLeveledUp] = useState(0);

  const [_subHeaderHeight, setSubHeaderHeight] = useState(0);

  const passages = locationState.passages.length ? locationState.passages : locationState.reviewPassages; // @TODO: is there a better way to make sure this doesn't blow up if only review passages are passed in?
  const reviewPassages = locationState.reviewPassages;
  const reviewPercent = (reviewPassages && reviewPassages.length) ? locationState.reviewPercent : 0;

  const lessonStartTimestamp = useMemo(() => {
    return Date.now();
  }, []);

  const theme = hooks.useTypedSelector((state) => state.theme);
  const stormMultiplier = useRef(theme === 'storm' ? 2 : 1); // if lesson was started during a storm, then give storm bonus

  const user = hooks.useTypedSelector((state) => state.oakUser);
  const activeGoal = hooks.useTypedSelector((state) => state.goals[state.settingsActiveGoalId]); // @TODO: why is `state.settingsActiveGoalId` `null` when reloading in Test? (to repro, complete a test, check but don't continue, then make a change to this file and save. probably not going to affect the end user, but it's scary that i don't know why that's happening)
  const inactiveMinigames = hooks.useTypedSelector((state) => state.inactiveMinigames);
  const availableMinigames = useMemo(() => {
    if (locationState.availableMinigamesOverride) {
      return minigames(i18n).filter((m) => locationState.availableMinigamesOverride.includes(m.id));
    }

    return minigames(i18n).filter((m) => !inactiveMinigames.includes(m.id));
  }, [inactiveMinigames, i18n, locationState.availableMinigamesOverride]);

  const goalSettings = hooks.useTypedSelector((state) => state.goalSettings);

  const difficulty = locationState.difficultyOverride ?? goalSettings.difficulty;

  const testDraft = hooks.useTypedSelector((state) => isTest ? state.testDrafts[passages[0]] : null); // @TODO: may need to change

  const wrinkles = useMemo(() => isTest ? (passages[0]?.getWrinkles() || []) : [], [isTest, passages]); // passages.length should always be one for tests // @TODO: got a crash where this was null...not sure why, but i added the null-check: https://console.firebase.google.com/u/0/project/wrinkly-prod/crashlytics/app/ios:com.oakwoodsc.wrinkly/issues/d9570d8c778e4f6a9a52a073ff28af34?time=last-ninety-days&sessionEventKey=fc42335af785469f87d2524fd403c5d3_1948209192124839948
  const unstartedWrinkles = useMemo(() => wrinkles.filter((w) => w.level_number === 0), [wrinkles]);
  const inProgressWrinkles = useMemo(() => wrinkles.filter((w) => w.level_number > 0 && w.level_number < globals.srsLevelWrinkleIsEarnedAt), [wrinkles]);
  const completedWrinkles = useMemo(() => wrinkles.filter((w) => w.level_number >= globals.srsLevelWrinkleIsEarnedAt), [wrinkles]);
  const inProgressWrinklesReady = useMemo(() => inProgressWrinkles.filter((w) => (Date.now() >= new Date(w.date_next_srs_level).valueOf())), [inProgressWrinkles]); // @TODO: abstract to a method on a Wrinkle class
  const completedWrinklesReady = useMemo(() => completedWrinkles.filter((w) => (Date.now() >= new Date(w.date_next_srs_level).valueOf())), [completedWrinkles]); // @TODO: abstract to a method on a Wrinkle class
  const inProgressWrinklesNotReady = useMemo(() => inProgressWrinkles.filter((w) => (Date.now() < new Date(w.date_next_srs_level).valueOf())), [inProgressWrinkles]); // @TODO: abstract to a method on a Wrinkle class
  const completedWrinklesNotReady = useMemo(() => completedWrinkles.filter((w) => (Date.now() < new Date(w.date_next_srs_level).valueOf())), [completedWrinkles]); // @TODO: abstract to a method on a Wrinkle class
  const ineligibleWrinkles = useMemo(() => {
    return [...inProgressWrinklesNotReady, ...completedWrinklesNotReady];
  }, [inProgressWrinklesNotReady, completedWrinklesNotReady]);

  const isPassageOfTheDayComplete = hooks.useTypedSelector((state) => !!(state.passageOfTheDay as any).isComplete);
  const isPassageOfTheDayCompleteInitial = useRef(isPassageOfTheDayComplete);

  const firstTime = (inProgressWrinkles.length === 0 && completedWrinkles.length === 0);
  const numVersesPeanutsOnly = (inProgressWrinkles.length + completedWrinkles.length) - (inProgressWrinklesReady.length + completedWrinklesReady.length);
  const [_infoDialogOpen, setInfoDialogOpen] = useState(isTest && (firstTime || (numVersesPeanutsOnly > 0)));

  const _minigameIsLegal = useCallback((minigame, thisPassage) => {
    if (!minigame) {
      // should be possible only if no minigames are turned on in settings
      return false;
    } else if (minigame.name === i18n('orderTheVerses')) {
      return thisPassage.start_verse_number !== thisPassage.end_verse_number;
    }

    // @TODO: if passage text is blank, return false

    return true;
  }, [i18n]);

  const [_isBackButtonHidden, setIsBackButtonHidden] = useState(false);

  const games: any = useMemo(() => {
    if (isTest) {
      return [
        {
          minigameId: 1,
          component: (
            <TestWrapper
              key='test'
              passage={passages[0]}
              subHeaderHeight={_subHeaderHeight}
              allWrinkles={wrinkles}
              autoFocus={!_infoDialogOpen}
              onCheckAnswer={() => {
                setIsBackButtonHidden(true);
              }}
              // @TODO: not being used right now {
              //onSaveDraft={(passageId, draftText, msSpentOnDraft) => {
              //  dispatch(setTestDraft(
              //    passageId,
              //    draftText,
              //    Date.now() - lessonStartTimestamp + msSpentOnDraft,
              //  ));
              //}}
              // @TODO: not being used right now }
              onPass={(numPeanutsAwarded, multiVerseBonus, levelUpBonus, passedTestVerseNums) => {
                setIsBackButtonHidden(false);
                if (numPeanutsAwarded) {
                  setNumCorrectAnswers(numPeanutsAwarded);
                }
                setTestPassedVerseNums(passedTestVerseNums);
                setMultiVerseBonus(multiVerseBonus);
                setLevelUpBonus(levelUpBonus);
                setCurrGameIndex((currGameIndex) => currGameIndex + 1);
                setNumVersesReviewed(passages[0].end_verse_number - passages[0].start_verse_number + 1);
                const ineligibleVerseNums = ineligibleWrinkles.map((wrinkle) => +wrinkle.verse_api_id.split('.')[2]);
                setNumVersesLeveledUp(passedTestVerseNums.filter((vn) => !ineligibleVerseNums.includes(vn)).length);
              }}
            />
          ),
        },
      ];
    }

    // @TODO: these should all be Game components...under that is different types of games
    // @TODO: randomly generated minigames; sample from whatever ones are toggled on in settings
    return [...Array(NUM_GAMES_IN_LESSON)].map((__, index) => {
      let minigame = availableMinigames[Math.floor(Math.random() * availableMinigames.length)];
      if (!minigame) {
        // should be possible only if no minigames are turned on in settings
        return null;
      }
      let x = 0;
      let passage = passages[Math.floor(Math.random() * passages.length)];
      if ((Math.random() * 100) < reviewPercent) {
        passage = reviewPassages[Math.floor(Math.random() * reviewPassages.length)];
      }

      while (!_minigameIsLegal(minigame, passage)) { // @TODO: i think this is causing flashing issues sometimes
        if (x >= 20) {
          return null;
        }
        minigame = availableMinigames[Math.floor(Math.random() * availableMinigames.length)];
        x += 1;
      }
      const GameComponent = minigame.component;
      return {
        minigameId: minigame.id,
        component: (
          <GameComponent
            key={index}// @TODO: better key
            difficulty={difficulty}
            subHeaderHeight={_subHeaderHeight}
            passage={passage}
            onCheckAnswer={() => setIsBackButtonHidden(true)}
            onPass={(isCorrect, numVersesReviewed) => {
              setIsBackButtonHidden(false);
              if (isCorrect) {
                setNumCorrectAnswers((curr) => curr + 1);
              }
              setCurrGameIndex((currGameIndex) => currGameIndex + 1);
              setNumVersesReviewed((curr) => curr + numVersesReviewed);
            }}
          />
        ),
      };
    }).filter((g) => g);
  }, [difficulty, passages, availableMinigames, isTest, _minigameIsLegal, _infoDialogOpen, wrinkles, ineligibleWrinkles, reviewPassages, reviewPercent, _subHeaderHeight]); // @TODO: _subHeaderHeight goes from undefined to 0 to 36 (on android), which is an extra render than i'd like...the first one flashes before it's replaced

  const gameOver = _currGameIndex >= games.length;

  const perfectBonus = (_numCorrectAnswers > 1 && _numCorrectAnswers === games.length) ? Math.round(_numCorrectAnswers / 4) : 0;
  const passageOfTheDayBonus = locationState.passageOfTheDayId && !isPassageOfTheDayCompleteInitial.current && gameOver ? PASSAGE_OF_THE_DAY_BONUS : 0;
  const totalPeanutsEarned = ((Math.round((_numCorrectAnswers + perfectBonus + _multiVerseBonus) * (isTest ? 1 : DIFFICULTY_MULTIPLIERS[difficulty])) + passageOfTheDayBonus) * stormMultiplier.current) + _levelUpBonus;

  const _saveLesson = (msSpentOnDraft = 0) => {
    let passageId = null;
    let adHocPassage = null;
    if (passages.length === 1) {
      passageId = passages[0].id;
      if (passageId === -1) { // @TODO: need to make sure all temp ids are updated to be -1
        ({...adHocPassage} = passages[0]);
      }
    }

    const assignmentId = locationState.assignmentId || null;

    utilities.saveLesson({
      userId: user.id,
      passageId,
      millisecondsSpent: Date.now() - lessonStartTimestamp + msSpentOnDraft,
      peanutsEarned: totalPeanutsEarned,
      difficulty: isTest ? 'test' : difficulty,
      numberOfVersesReviewed: _numVersesReviewed,
      numberOfQuestions: isTest ? 0 : _currGameIndex,
      minigameIds: isTest ? [1] : availableMinigames.map((m) => m.id),
      goalId: locationState.noGoal ? null : activeGoal.id,
      testPassedVerseNums: _testPassedVerseNums,
      assignmentId,
      adHocPassage,
      streakRepairDate: locationState.repairStreakDate,
      passageOfTheDayId: locationState.passageOfTheDayId,
      numberOfSkippedQuestions: _numSkippedQuestions,
    });
  };

  hooks.useBackButton(() => {
    if (gameOver) {
      return true; // @TODO: does this disable on android?
    } else if (_currGameIndex > 0 && !isTest) {
      _saveLesson();
    }
    return false;
  });

  const backIcon = (
    <Header.BackIcon
      onPress={() => {
        if (!isTest && !gameOver && _currGameIndex > 0) {
          _saveLesson();
        }
        navigation.goBack();
      }}
    />
  );

  const infoIcon = (
    <Header.Icon
      icon='information'
      onPress={() => {
        Keyboard.dismiss();
        setInfoDialogOpen(true);
      }}
    />
  );

  const _renderMinigameType = () => { // @TODO: this is janky on open
    if (isTest) { // @TODO: probably don't need this...can show instructions same as other game types
      return null;
    }

    const minigame: any = availableMinigames.find((m) => m.id === games[_currGameIndex].minigameId);

    return (
      <Popover.Wrapper
        disabled={!minigame.description && !minigame.instructions?.length}
        actions={[
          {
            text: i18n('okay'),
          },
        ]}
        popover={
          <Popover.Inner>
            <Popover.Text title={true}>{minigame.name}</Popover.Text>
            <Popover.Text>{minigame.description}</Popover.Text>
            {minigame.instructions.map((instruction, index) => {
              return <Popover.Text key={index}>{instruction}</Popover.Text>;
            })}
          </Popover.Inner>
        }
      >
        <View style={styles.headerOption} >
          <Icon
            style={styles.headerOptionIcon}
            name='help-circle'
          />
          <Text style={styles.headerOptionText}>{minigame.name}</Text>
        </View>
      </Popover.Wrapper>
    );
  };

  const _renderSkip = () => { // @TODO: this is janky on open
    if (isTest) { // @TODO: probably don't need this...can show instructions same as other game types
      return null;
    }

    return (
      <Popover.Wrapper
        actions={[
          {
            text: i18n('skip'),
            onPress: () => {
              setCurrGameIndex((currGameIndex) => currGameIndex + 1); // @TODO: would be nice to show the correct answer as well...maybe incorrect loader on next button...
              setNumSkippedQuestions((curr) => curr + 1);
            },
          },
        ]}
        popover={
          <Popover.Inner>
            <Popover.Text title={true}>{i18n('confirmSkip')}</Popover.Text>
            <Popover.Text>{i18n('confirmSkipDescription')}</Popover.Text>
          </Popover.Inner>
        }
      >
        <View style={styles.headerOption}>
          <Text style={styles.headerOptionText}>{i18n('skip')}</Text>
          <Icon
            style={styles.headerOptionIcon}
            name='skip-next'
          />
        </View>
      </Popover.Wrapper>
    );
  };

  const prettyReference = passages[0].prettyPassageName();

  const numWrinklesReadyToLevelUpAgain = inProgressWrinklesReady.length + completedWrinklesReady.length;

  let leftItems = !_isBackButtonHidden ? [backIcon] : [];
  let rightItems = isTest ? [infoIcon] : [];
  if (gameOver) {
    leftItems = [];
    rightItems = [];
  }

  return (
    <ScreenWrapper>
      <Header
        leftItems={leftItems}
        rightItems={rightItems}
      />
      <FullScreenScrollView
        contentContainerStyle={styles.outerContainer}
        noScroll={true}
      >{/* @TODO: not scrollable...this is just to make it full screen height...test this with long passages */}{/* @TODO: prefer to make this scrollEnabled={false}, but i need to make the verse text scrollable first (try on extra large with Esther 8:9)...mobile is fine, but web is bad */}
        <View style={styles.innerContainerWrapper}>
          {!isTest &&
            <View style={styles.progressBarWrapper}>
              <ProgressBar
                key={styles.windowWidth}// when ipads change orientation from landscape to portrait, we need to re-render the progress bar
                style={styles.progressBar}
                progress={_currGameIndex / (games.length || 1)}
                color={styles.colors.white}
              />
            </View>
          }
          <View style={styles.innerContainer}>
            <View style={styles.gameView}>
              <View
                onLayout={(e) => {
                  setSubHeaderHeight(Math.round(e.nativeEvent.layout.height + styles.headerOptionsWrapper.marginBottom + 10)); // @TODO: don't know where this 10 comes from... // @TODO: toggles between 35.999996185302734 and 36 on different renders, so this keeps it a bit more stable...but should this be a ref instead of state?
                }}
                style={styles.headerOptionsWrapper}
              >
                {!gameOver && _renderMinigameType()}
                {!gameOver && _renderSkip()}
              </View>
              {(games.length === 0 || availableMinigames.length === 0)
                ? (
                  <EmptyScreen.Wrapper>
                    <EmptyScreen.Text>
                      {availableMinigames.length === 0
                        ? i18n('noMinigamesSelected')
                        : i18n('minigamesNotAvailable')
                      }
                    </EmptyScreen.Text>
                    <Button
                      mode='contained'
                      theme={styles.colors.accent}
                      onPress={() => navigation.goBack()}
                    >
                      {i18n('back')}
                    </Button>
                  </EmptyScreen.Wrapper>
                )
                : (!gameOver
                  ? (
                    games[_currGameIndex].component
                  ) : (
                    <SummaryScreen
                      numCorrectAnswers={_numCorrectAnswers}
                      numTotalAnswers={games.length}
                      totalPeanutsEarned={totalPeanutsEarned}
                      perfectBonus={perfectBonus}
                      passageOfTheDayBonus={passageOfTheDayBonus}
                      // @TODO: 4 peanut bonus for the first review of the day
                      numVersesLeveledUp={_numVersesLeveledUp}
                      multiVerseBonus={_multiVerseBonus}
                      levelUpBonus={_levelUpBonus}
                      difficulty={difficulty}// @TODO: dynamic
                      difficultyBonus={DIFFICULTY_MULTIPLIERS[difficulty]}// @TODO: dynamic...shouldn't need to pass this in (this should be from back end though so i can configure it remotely (happy hours and stuff))
                      stormMultiplier={stormMultiplier.current}
                      passages={passages}
                      isTest={isTest}
                      repairStreakDate={locationState.repairStreakDate}
                      passedTestVerseNums={_testPassedVerseNums}
                      onComplete={() => {
                        // @TODO: should be saving each one as we go? makes point calculation harder though...
                        let msSpentOnDraft = 0;
                        if (testDraft) {
                          msSpentOnDraft = testDraft.msSpent;
                        }
                        _saveLesson(msSpentOnDraft);
                      }}
                    />
                  )
                )
              }
            </View>
          </View>
          {_infoDialogOpen &&
            <Dialog
              visible={_infoDialogOpen}
              onDismiss={() => setInfoDialogOpen(false)}
            >
              <Dialog.Title><Text style={styles.dialogTitle}>{i18n('testInfo')}</Text></Dialog.Title>{/* @TODO: shouldn't need Text component here...all dialogs should be bold on their own by default*/}
              <Dialog.Content>
                <View style={styles.dialogInnerScrollWrapper}>
                  {firstTime &&
                    <React.Fragment>
                      <Text style={styles.dialogText}>{i18n('congratulationsReady', prettyReference)}</Text>
                      <Text style={styles.dialogText}>{i18n('threeLevelsNotice')}</Text>
                      <Text style={styles.dialogText}>{i18n('testInstructions')}</Text>
                    </React.Fragment>
                  }
                  {!!unstartedWrinkles.length &&
                    <Text style={styles.dialogText}>
                      <Text style={styles.bold}>{unstartedWrinkles.length} {unstartedWrinkles.length === 1 ? i18n('verseIs') : i18n('versesAre')} </Text>{i18n('readyToBeTestedFirst')}
                    </Text>
                  }
                  {!!numWrinklesReadyToLevelUpAgain &&
                    <Text style={styles.dialogText}>
                      <Text style={styles.bold}>{numWrinklesReadyToLevelUpAgain} {numWrinklesReadyToLevelUpAgain === 1 ? i18n('verseIs') : i18n('versesAre')} </Text>{i18n('readyToBeTestedAgain')}
                    </Text>
                  }
                  {(numVersesPeanutsOnly > 0) &&
                    <Text style={styles.dialogText}>
                      <Text style={styles.bold}>{numVersesPeanutsOnly} {numVersesPeanutsOnly === 1 ? i18n('inProgressVerse') : i18n('inProgressVerses')} </Text>{i18n('canBePracticed')}{/* @TODO: watch out for puede/pueden in spanish */}
                    </Text>
                  }
                  <Text style={styles.dialogText}>{i18n('youveGotThis')}</Text>
                  <PassageDisplay
                    passages={passages}
                    hideLicensing={true}
                    showFormatting={false}
                    showFootnotes={false}
                    showHeadings={false}
                    hideCompare={true}
                    hideAddToGoal={true}
                    verseNumbersOnly={true}
                    alwaysShowSelectionMenu={false}
                    isPressDisabled={true}
                    showWrinkleBadges={true}
                    isBadgePressDisabled={true}
                  />
                  {/* @TODO:
                    <Text style={styles.dialogText}>{"TODO: do spelling and punctuation count? you will not be penalized for incorrect punctuation--as long as the words are separated by spaces, {TODO: wrinkly mascot name here} should be able to figure out what you're typing; he'll do his best to ignore spelling mistakes, but he has an harder time figuring out spelling mistakes than issues with punctuation"}</Text>
                    <Text style={styles.dialogText}>TODO: spell out numbers and stuff like that</Text>
                  */}
                  {/* @TODO: more faqs...actually just link to these maybe */}
                </View>
              </Dialog.Content>
              <Dialog.Actions>
                <Button
                  mode='contained'
                  onPress={() => {
                    // @TODO: implement
                    setInfoDialogOpen(false);
                  }}
                >
                  {i18n('resume')}
                </Button>
              </Dialog.Actions>
            </Dialog>
          }
        </View>
      </FullScreenScrollView>
    </ScreenWrapper>
  );
};

export default Practice;
